import React, { useEffect, useState, useRef } from "react";
import Youroheader from "../Youro-header";
import { COOKIE_KEYS } from "../../App";
import Cookies from "js-cookie";
import Loader from "../../utils/loader";
import ChatMessage from "../ChatMessage";
import { useChatContext } from "../../context/ChatContext";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft } from "@fortawesome/free-solid-svg-icons";

// DESIGN_SYSTEM.md section 3: Nunito for headers, Inter for everything else.
const FONT_HEADER = "'Nunito', 'Helvetica Neue', Arial, sans-serif";
const FONT_BODY = "'Inter', 'Helvetica Neue', Arial, sans-serif";

const PatientChat = (props) => {
  const {
    stompObject,
    connectionStatus,
    chatUserDetails,
    totalMsgCount,
    updateCount,
    updateChatUserDetails,
    selectedChat,
    setSelectedChat,
    chatData,
    setChatData,
  } = useChatContext();

  // ---------------- Palette / shared styles ----------------
  // Mapped onto the DESIGN_SYSTEM.md tokens rather than a private set of hexes,
  // so this screen stays in step with the rest of the platform.
  const palette = {
    pageBg: "var(--youro-sand)",
    cardBg: "var(--card)",
    cardBorder: "var(--border)", // Charcoal @ 10%, per the card spec
    listCardBg: "var(--card)",
    primaryText: "var(--youro-charcoal)",
    subText: "rgba(46, 46, 46, 0.65)", // Charcoal, muted — the spec has no separate sub-text token
    accentGreen: "var(--youro-mint)",
    accentGreenBorder: "var(--youro-sage)",
    sendBtn: "var(--youro-coral)", // Coral = the one primary action on this screen
    divider: "var(--border)",
    scrollThumb: "var(--youro-stone)",
  };

  const textInputStyle = {
    borderRadius: 10, // spec border-radius
    border: "1px solid var(--border)",
    padding: "9px 16px",
    fontSize: 14,
    fontFamily: FONT_BODY,
    width: "100%",
    outline: "none",
    backgroundColor: "var(--card)",
  };

  const titles = {
        0: "Home",
        1: "Appointments",
        2: "Messaging",
        3: "Education",
        5: "Prescriptions",
    };

  // ---------------- Local state ----------------
  const [viewVal, setViewVal] = useState(0);
  const [chatHistory, setChatHistory] = useState([]);
  const [message, setMessage] = useState("");
  const [isLoading, setIsloading] = useState(false);
  const [searchInput, setSearchInput] = useState("");
  const [searchData, setSearchData] = useState(null);
  const [activeLoader, setActiveLoader] = useState(true);
  const [selectedChatName, setSelectedChatName] = useState("");

  const [isMobile, setIsMobile] = useState(window.innerWidth <= 600);

  const navToProfile = (viewVal) => {
    props.changeView(viewVal);
  };

  useEffect(() => {
    if (viewVal === 4 || viewVal === 6) {
      navToProfile(viewVal);
    }
  }, [viewVal]);

  // ---------------- WebSocket / chat users ----------------
  const onReceivedChatUsers = (payload) => {
    const res = JSON.parse(payload.body);
    const chatUsers = [];

    res.forEach((x) => {
      const modObj = {
        name: x.fullName,
        picture: x.image,
        email: x.userEmail,
        uId: x.userId,
      };

      if (chatUserDetails[x.userId]) {
        modObj["message"] = chatUserDetails[x.userId].message;
      }

      chatUsers.push(modObj);
    });

    setSearchData(chatUsers);
    setChatHistory(chatUsers);
  };

  const listenToGetChat = () => {
    stompObject.subscribe(
      `/user/${Cookies.get(COOKIE_KEYS.userId)}/chat-data`,
      onReceivedChatData
    );
  };

  const fetchChatUsers = () => {
    stompObject.send(`/app/getChatUsers/${Cookies.get(COOKIE_KEYS.userId)}`);
  };

  const listenToChatUsers = () => {
    stompObject.subscribe(
      `/user/${Cookies.get(COOKIE_KEYS.userId)}/chat-users`,
      onReceivedChatUsers
    );
  };

  // connectionStatus is real state in ChatContext, so a connection change actually
  // re-renders here. Watching stompObject.connected instead would miss the
  // transition, which is what used to leave this screen on a permanent spinner.
  useEffect(() => {
    if (connectionStatus === "connected") {
      setActiveLoader(false);
      listenToChatUsers();
      fetchChatUsers();
      listenToGetChat();
      return;
    }

    // Failed or retrying: drop the loader and say so inline. Covering an open
    // conversation with a full-screen spinner because the socket blipped would be
    // worse than the interruption itself.
    if (connectionStatus === "failed" || connectionStatus === "reconnecting") {
      setActiveLoader(false);
      return;
    }

    // idle / connecting — keep the loader up.
    setActiveLoader(true);
  }, [connectionStatus]);

  useEffect(() => {
    if (!chatUserDetails || Object.keys(chatUserDetails).length === 0) return;
    fetchChatUsers();
  }, [chatUserDetails]);

  const divRef = useRef(null);
  // Same as the provider chat: jump to the newest message when a conversation
  // opens, animate only for messages that arrive afterwards.
  const justOpened = useRef(true);

  useEffect(() => {
    justOpened.current = true;
  }, [selectedChat]);

  useEffect(() => {
    if (!divRef.current) return;
    divRef.current.scrollIntoView({
      behavior: justOpened.current ? "auto" : "smooth",
      block: "end",
    });
    justOpened.current = false;
  }, [chatData]);

  const getChat = (userId) => {
    const sendUrl = `/app/getChat`;
    const data = {
      receiverId: Cookies.get(COOKIE_KEYS.userId),
      senderId: userId,
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    };
    stompObject.send(sendUrl, {}, JSON.stringify(data));
  };

  const onReceivedChatData = (payload) => {
    const res = JSON.parse(payload.body);
    setChatData(res);
  };

  const saveChat = () => {
    if (message.trim()) {
      const msg = message.trim();
      const fromId = parseInt(Cookies.get(COOKIE_KEYS.userId));
      const toId = selectedChat;
      const sendTime = new Date();

      const data = { msg, from: fromId, to: toId, time: sendTime };
      stompObject.send("/app/saveChat", {}, JSON.stringify(data));

      const mssg = { message: msg, fromId, toId, time: sendTime, seen: false };

      const updatedChatUserDetails = {
        ...chatUserDetails,
        [toId]: {
          ...chatUserDetails[toId],
          message: msg,
        },
      };
      updateChatUserDetails(updatedChatUserDetails);

      const newData = [mssg, ...chatData];
      setChatData(newData);
    }
    setMessage("");
    setIsloading(false);
  };

  const updateChatHistory = (userId) => {
    // Same guard as the provider chat: chatUserDetails is undefined until the
    // socket delivers the user list.
    if (!chatUserDetails || !chatUserDetails[userId]) return;

    updateCount(totalMsgCount - chatUserDetails[userId].count);
    chatUserDetails[userId].count = 0;
    updateChatUserDetails(chatUserDetails);
  };

  let userIdToFullName = {};
  const ChatNamesUi = (props) => {
    userIdToFullName[props.data.uId] = props.data.name;

    const isActive = props.data.uId === selectedChat;

    const cardBase = {
      borderRadius: 16,
      padding: "12px 14px",
      display: "flex",
      cursor: "pointer",
      border: `1px solid ${
        isActive ? palette.accentGreenBorder : palette.cardBorder
      }`,
      backgroundColor: isActive ? palette.accentGreen : palette.cardBg,
      marginBottom: 12,
      boxShadow: isActive ? "0 4px 10px rgba(0,0,0,0.04)" : "none",
      transition: "background 0.15s, box-shadow 0.15s, border 0.15s",
    };

    return (
      <div
        style={cardBase}
        onClick={() => {
          setSelectedChat(props.data.uId);
          setSelectedChatName(props.data.name);
          getChat(props.data.uId);
          updateChatHistory(props.data.uId);
        }}
      >
        <div
          style={{
            marginRight: 12,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <div
            style={{
              height: 40,
              width: 40,
              borderRadius: "50%",
              backgroundColor: "var(--youro-sage)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <img
              src={require("../../assets/Display_icon.png")}
              alt="Patient avatar"
              style={{ height: 26, width: 26, borderRadius: "50%" }}
            />
          </div>
        </div>
        <div style={{ flex: 1, overflow: "hidden" }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              marginBottom: 2,
            }}
          >
            <p
              style={{
                margin: 0,
                fontWeight: 600,
                fontSize: 14,
                color: palette.primaryText,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {props.data.name}
            </p>
            {chatUserDetails[props.data.uId]?.count ? (
              <span
                style={{
                  minWidth: 18,
                  height: 18,
                  borderRadius: 999,
                  backgroundColor: "var(--youro-sage)",
                  color: "#fff",
                  fontSize: 11,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  marginLeft: 6,
                }}
              >
                {chatUserDetails[props.data.uId].count}
              </span>
            ) : null}
          </div>
          <p
            style={{
              margin: 0,
              fontSize: 13,
              color: palette.subText,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {chatUserDetails[props.data.uId] ? (
              chatUserDetails[props.data.uId].message
            ) : (
              <span style={{ fontSize: 12 }}>Start conversation</span>
            )}
          </p>
          {/* optional timestamp placeholder */}
          {/* <p style={{ margin: 0, fontSize: 11, color: "#a2998b", marginTop: 4 }}>2 hours ago</p> */}
        </div>
      </div>
    );
  };

  const getFullNameById = (userId) => {
    return userIdToFullName[userId];
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      setIsloading(true);
      saveChat();
    }
  };

  const loadingClass = isLoading ? "chatApp__convButton--loading" : "";
  const sendButtonIcon = !isLoading ? (
    <span className="material-symbols-outlined">send</span>
  ) : (
    ""
  );

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 600);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // --------------- JSX ---------------
  return (
    // Fills the shell's content area rather than the viewport. The chat sits
    // below the sticky header, so sizing it to 100vh made it taller than the
    // space it had by exactly the header's height, pushing the composer and its
    // send button off the bottom of the screen.
    <div style={{ width: "100%", height: "100%", display: "flex", flexDirection: "column", minHeight: 0 }}>
      <Loader active={activeLoader} />
      {(connectionStatus === "failed" || connectionStatus === "reconnecting") && (
        <div
          style={{
            margin: "12px 20px 0",
            padding: "14px 16px",
            borderRadius: 10,
            // Neutral card treatment, not a severity colour: DESIGN_SYSTEM.md
            // reserves the severity palette for symptom scores, and defines no
            // error/alert colour. Worth adding one deliberately rather than
            // improvising a fifth colour here.
            border: "1px solid var(--youro-border)",
            backgroundColor: "var(--card)",
            color: "var(--youro-charcoal)",
            fontFamily: FONT_BODY,
            fontSize: 14,
          }}
        >
          {connectionStatus === "reconnecting"
            ? "Reconnecting to messaging… new messages may be delayed until this finishes."
            : "We couldn't connect to messaging. Check your connection and refresh the page — your conversations are safe."}
        </div>
      )}
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          margin: "0 20px 12px",
          //width: "100%",
          flex: 1,
          minHeight: 0,
          overflow: "hidden",
          //backgroundColor: palette.pageBg,
        }}
      >

        <div
          style={{
            flexGrow: 1,
            display: "flex",
            flexDirection: "column",
            minHeight: 0,
            overflow: "hidden",
          }}
        >
          {/* Page Title */}
          <p
            style={{
              margin: "4px 0 9px 10px",
              fontSize: 24,
              fontWeight: 500,
              color: palette.primaryText,
              fontSize: "28px",
            }}
          >
            Messages
          </p>

          {/* Two-column layout */}
          <div
            style={{
              flex: 1,
              display: "flex",
              flexDirection: isMobile ? "column" : "row",
              gap: 24,
              minHeight: 0,
              overflow: "hidden",
            }}
          >
            {/* LEFT – Conversations */}
            <div
              style={{
                flex: isMobile ? "0 0 auto" : "0 0 320px",
                display:
                  !selectedChat || !isMobile ? "block" : "none",
                minHeight: 0,
              }}
            >
              <div
                style={{
                  backgroundColor: palette.cardBg,
                  borderRadius: 20,
                  border: `1px solid ${palette.cardBorder}`,
                  boxShadow: "0 4px 18px rgba(0,0,0,0.03)",
                  padding: "18px 18px 18px",
                  height: isMobile ? "auto" : "100%",
                  display: "flex",
                  flexDirection: "column",
                  minHeight: 0,
                }}
              >
                <p
                  style={{
                    margin: "0 0 14px",
                    fontSize: 16,
                    fontWeight: 600,
                    color: palette.primaryText,
                    fontSize: "1rem !important"
                  }}
                >
                  Conversations
                </p>

                {/* Search bar */}
                <div style={{ marginBottom: 14 }}>
                  <input
                    type="text"
                    style={textInputStyle}
                    onChange={(e) => setSearchInput(e.target.value)}
                    placeholder="Search or start new chat"
                    value={searchInput}
                  />
                </div>

                {/* List of chats */}
                <div
                  style={{
                    flex: 1,
                    overflowY: "auto",
                    paddingRight: 4,
                    minHeight: 0,
                  }}
                >
                  {!searchInput &&
                    chatHistory &&
                    chatHistory.map((data, idx) => (
                      <ChatNamesUi data={data} key={idx} />
                    ))}

                  {((chatHistory && !chatHistory[0]) || searchInput) &&
                    searchData &&
                    searchData.map((data, idx) => {
                      const matches =
                        data.name
                          .toLowerCase()
                          .includes(searchInput.toLowerCase()) ||
                        (data.email &&
                          data.email
                            .toLowerCase()
                            .includes(searchInput.toLowerCase()));
                      return matches ? (
                        <ChatNamesUi data={data} key={idx} />
                      ) : null;
                    })}
                </div>
              </div>
            </div>

            {/* RIGHT – Chat panel */}
            <div
              style={{
                flex: 1,
                minHeight: 0,
              }}
            >
              <div
                style={{
                  backgroundColor: palette.cardBg,
                  borderRadius: 20,
                  border: `1px solid ${palette.cardBorder}`,
                  boxShadow: "0 4px 18px rgba(0,0,0,0.03)",
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  minHeight: 0,
                }}
              >
                {selectedChat ? (
                  <>
                    {/* Header */}
                    <div
                      style={{
                        padding: "16px 22px 14px",
                        borderBottom: `1px solid ${palette.cardBorder}`,
                        display: "flex",
                        alignItems: "center",
                        gap: 12,
                      }}
                    >
                      {isMobile && (
                        <button
                          onClick={() => setSelectedChat(null)}
                          style={{
                            border: "none",
                            background: "transparent",
                            marginRight: 4,
                            cursor: "pointer",
                            padding: 4,
                          }}
                        >
                          <FontAwesomeIcon
                            icon={faArrowLeft}
                            style={{ color: palette.subText }}
                          />
                        </button>
                      )}
                      <div
                        style={{
                          height: 40,
                          width: 40,
                          borderRadius: "50%",
                          backgroundColor: "var(--youro-sage)",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <img
                          src={require("../../assets/Display_icon.png")}
                          alt="Doctor avatar"
                          style={{ height: 26, width: 26, borderRadius: "50%" }}
                        />
                      </div>
                      <div>
                        <div
                          style={{
                            fontSize: 15,
                            fontWeight: 600,
                            color: palette.primaryText,
                          }}
                        >
                          {getFullNameById(selectedChat) || selectedChatName}
                        </div>
                      </div>
                    </div>

                    {/* Messages scroll area */}
                    <div
                      id="chat-scroll"
                      style={{
                        flex: 1,
                        overflowY: "auto",
                        padding: "18px 24px 8px",
                        minHeight: 0,
                        overscrollBehavior: "contain",      // ✅ prevents parent/page scroll
                        WebkitOverflowScrolling: "touch",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column-reverse",
                        }}
                      >
                        <div ref={divRef}></div>
                        {chatData &&
                          chatData.map((data, i) => {
                            const timestamp = new Date(data.time);
                            const k = chatData[i + 1]
                              ? new Date(chatData[i + 1].time)
                              : "";
                            const prevTimeStamp = k
                              ? k.toLocaleDateString()
                              : "";

                            return (
                              <ChatMessage
                                key={`${data.time}-${i}`}
                                data={data}
                                timestamp={timestamp}
                                prevTimeStamp={prevTimeStamp}
                                stompObject={stompObject}
                                selectedChat={selectedChat}
                              />
                            );
                          })}

                        {chatData && !chatData[0] && (
                          <div
                            style={{
                              marginTop: 40,
                              display: "flex",
                              justifyContent: "center",
                            }}
                          >
                            <p
                              style={{
                                textAlign: "center",
                                fontSize: 18,
                                color: palette.subText,
                              }}
                            >
                              <strong>Say &quot;Hello&quot;</strong>
                            </p>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Divider */}
                    <div
                      style={{
                        borderTop: `1px solid ${palette.cardBorder}`,
                        margin: "0 24px",
                      }}
                    />

                    {/* Input / send */}
                    <div
                      style={{
                        padding: "16px 24px 10px",
                        display: "flex",
                        flexDirection: "column",
                        gap: 8,
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "flex-end",
                          gap: 12,
                        }}
                      >
                        <textarea
                          rows={1}
                          onKeyDown={handleKeyDown}
                          style={{
                            flex: 1,
                            borderRadius: 10,
                            border: "1px solid var(--border)",
                            padding: "10px 14px",
                            resize: "none",
                            fontSize: 14,
                            fontFamily: FONT_BODY,
                            outline: "none",
                            backgroundColor: "var(--card)",
                          }}
                          placeholder="Type your message..."
                          onChange={(e) => setMessage(e.target.value)}
                          value={message}
                        />
                        <button
                          className={loadingClass}
                          onClick={() => {
                            setIsloading(true);
                            saveChat();
                          }}
                          style={{
                            borderRadius: 14,
                            border: "none",
                            minWidth: 46,
                            height: 46,
                            backgroundColor: palette.sendBtn,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            cursor: "pointer",
                          }}
                        >
                          {sendButtonIcon}
                        </button>
                      </div>
                      <div
                        style={{
                          fontSize: 11,
                          color: palette.subText,
                          marginLeft: 4,
                        }}
                      >
                        Press Enter to send, Shift + Enter for new line
                      </div>
                    </div>
                  </>
                ) : (
                  // Empty state when no conversation selected
                  <div
                    style={{
                      flex: 1,
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      padding: 40,
                    }}
                  >
                    <img
                      src={require("../../assets/no_selection_chat.png")}
                      alt="No selected chat"
                      style={{ height: 180, marginBottom: 20 }}
                    />
                    <p
                      style={{
                        textAlign: "center",
                        fontSize: 18,
                        color: palette.subText,
                      }}
                    >
                      <strong>Select a conversation to start messaging</strong>
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PatientChat;
